import React, {
  useEffect, useRef, useState,
} from 'react';
import { Text } from '@gluestack-ui/themed';

import { DASHBOARD_MOTION, easeOutCubic } from './dashboardMotion';

let instantFigureMotion = typeof process !== 'undefined' && process.env.JEST_WORKER_ID != null;

/** Lets motion unit tests run real count-up while screen tests stay deterministic. */
export function setFigureMotionInstantForTests(instant: boolean): void {
  instantFigureMotion = instant;
}

export type AnimatedFigureValueProps = {
  numericValue: number | null;
  formatValue: (value: number) => string;
  reduceMotion: boolean;
  /** Stagger before count-up starts (ms). */
  delayMs?: number;
  color: string;
  testID: string;
};

/**
 * Counts a figure headline from the previous numeric value to the next.
 * Reduced motion shows the formatted final value on first paint.
 */
export function AnimatedFigureValue({
  numericValue,
  formatValue,
  reduceMotion,
  delayMs = 0,
  color,
  testID,
}: AnimatedFigureValueProps) {
  const isMissing = numericValue == null || Number.isNaN(numericValue);
  const target = isMissing ? null : numericValue;

  /** Last target committed when a count starts (next count begins here). */
  const fromRef = useRef(0);
  const [display, setDisplay] = useState(() => {
    if (isMissing) return '—';
    if (reduceMotion) return formatValue(target as number);
    return formatValue(fromRef.current);
  });

  useEffect(() => {
    if (isMissing) {
      setDisplay('—');
      return undefined;
    }
    const next = target as number;
    if (reduceMotion || instantFigureMotion) {
      setDisplay(formatValue(next));
      fromRef.current = next;
      return undefined;
    }

    const from = fromRef.current;
    fromRef.current = next;

    let raf = 0;
    let startTime: number | undefined;
    let finished = false;
    const tick = (now: number) => {
      if (finished) return;
      if (startTime === undefined) startTime = now + delayMs;
      const elapsed = now - startTime;
      const progress = easeOutCubic(elapsed / DASHBOARD_MOTION.figureCountUpMs);
      const current = from + (next - from) * progress;
      setDisplay(formatValue(current));
      if (progress < 1) {
        raf = requestAnimationFrame(tick);
      } else {
        finished = true;
      }
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [delayMs, formatValue, isMissing, reduceMotion, target]);

  return (
    <Text
      fontFamily="$heading"
      color={color}
      testID={testID}
      sx={{
        fontSize: 32,
        lineHeight: 36,
        fontWeight: '600',
        _web: { fontVariantNumeric: 'tabular-nums' },
      }}
      style={{ fontVariant: ['tabular-nums'] }}
    >
      {display}
    </Text>
  );
}
